/**
 * DesignerNodeShell — Shared card shell for all designer nodes
 *
 * Encapsulates the ~90% shared UI pattern:
 * - Border color state machine (selected > running > hovered > default)
 * - Image area 4-state rendering (empty, running-no-image, running-blur, displayed)
 * - Sequenced loading animation (shimmer → typewriter)
 * - Error footer (2-line clamp + hover)
 * - Fullscreen lightbox (portal to document.body)
 * - Handle rendering (invisible 2x2px, from HandleConfig[])
 * - AgentNodeToolbar integration (Variants conditional, Duplicate, Delete)
 *
 * Per-designer content (name/desc area, quick info bar, bottom panel)
 * is provided via the `children` slot.
 *
 * Sprint 3a: Designer Nodes — Shared Foundation
 */

import React, { memo, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Handle } from 'reactflow'
import { AnimatePresence, motion } from 'framer-motion'
import { BookmarkPlus, MessageSquarePlus } from 'lucide-react'
import { getThumbnailUrl, getAssetUrl } from '../../../../api/useAssetUrl'
import { useNodeHover } from '../../NodeHoverContext'
import { AgentNodeToolbar, useNodeActions, type ToolbarAction } from './'
import { computeBorderStyle } from './designerUtils'
import type { HandleConfig } from './types'
import type { AgentColorConfig } from '../../../../constants/agentColors'
import { cn } from '../../../../lib/utils'
import type { NodeStatus } from '../../../../generated/nodeDataTypes'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createDebug } from '../../../../utils/debugLog'

const sizeLog = createDebug('dw:size:designer-shell')

export interface DesignerNodeShellProps {
  /** ReactFlow node ID */
  nodeId: string
  /** Whether the node is selected */
  selected: boolean | undefined
  /** Whether the node is being dragged */
  dragging: boolean | undefined
  /** Node status */
  status: NodeStatus
  /** Error message if status === 'error' */
  error?: string
  /** Progress message during running state */
  progressMessage?: string
  /** Node name for "Creating {name}..." message */
  nodeName?: string
  /** Agent type label (e.g., "Character", "Location") for running/empty messages */
  agentTypeLabel: string
  /** Agent color config from AGENT_COLORS */
  agentColor: AgentColorConfig
  /** Card width in px */
  width: number
  /** CSS class for image aspect ratio (e.g., 'aspect-video', 'aspect-[4/3]') */
  imageAspect: string
  /** Handle definitions */
  handles: HandleConfig[]
  /** data-testid for the card container */
  testId?: string
  /** Resolved image path (from resolveDesignerImagePath) */
  imagePath?: string
  /** Cache key for URL cache busting */
  cacheKey?: string
  /** Whether history has more than 1 entry */
  hasHistory: boolean
  /** Empty state icon (SVG path d attribute) */
  emptyIconPath: string
  /** Size class for the empty icon (e.g., 'w-8 h-8', 'w-10 h-10') */
  emptyIconSize?: string
  /** Callback to open the Variants tab in the bottom panel */
  onOpenVariants?: () => void
  /** Per-designer content area (name/desc, quick info bar, etc.) */
  children: React.ReactNode
  /** Bottom panel rendered inside the relative wrapper but outside the card (avoids overflow-hidden clipping) */
  bottomPanel?: React.ReactNode
  /** Floating label rendered above the card (e.g., node name) */
  floatingLabel?: React.ReactNode
}

const DesignerNodeShell = memo(function DesignerNodeShell({
  nodeId,
  selected,
  dragging,
  status,
  error,
  progressMessage,
  nodeName,
  agentTypeLabel,
  agentColor,
  width,
  imageAspect,
  handles,
  testId,
  imagePath,
  cacheKey,
  hasHistory,
  emptyIconPath,
  emptyIconSize = 'w-8 h-8',
  onOpenVariants,
  children,
  bottomPanel,
  floatingLabel,
}: DesignerNodeShellProps) {
  const projectId = useProjectId()
  const { handleDelete, handleDuplicate, handleDownload, handleAddToChat, handleSaveToLibrary } = useNodeActions({ nodeId })
  const { setHoveredNodeId } = useNodeHover()
  const [isHovered, setIsHovered] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const spinClass = isRunning ? 'animate-spin' : isPendingCost ? '' : 'animate-spin-slow'

  // Diagnostic logging for rendered node width
  sizeLog('[size] DesignerNodeShell: nodeId=%s width=%d', nodeId, width)

  // Image URL resolution
  const imageUrl =
    imagePath && projectId ? getThumbnailUrl(projectId, imagePath, 0, cacheKey) : null
  const fullImageUrl =
    imagePath && projectId ? getAssetUrl(projectId, imagePath, 0) : null

  // Border color state machine
  const borderStyle = computeBorderStyle(!!selected, status, isHovered, agentColor)

  // Download handler
  const onDownload = useCallback(() => {
    if (fullImageUrl) {
      handleDownload(
        fullImageUrl,
        imagePath?.split('/').pop() || `${agentTypeLabel.toLowerCase()}.jpg`
      )
    }
  }, [fullImageUrl, imagePath, handleDownload, agentTypeLabel])

  // Toolbar actions
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'history',
        label: 'Variants',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        ),
        onClick: () => onOpenVariants?.(),
        hidden: !hasHistory,
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger' as const,
      },
    ],
    [hasHistory, handleDuplicate, handleDelete, handleAddToChat, handleSaveToLibrary, onOpenVariants]
  )

  return (
    <>
      <div className="relative">
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

        {/* Floating label above the card */}
        {floatingLabel}

        {/* Main Card */}
        <div
          data-testid={testId}
          className={cn(
            'relative rounded-[24px] border bg-surface-0 transition-all duration-200 overflow-hidden cursor-pointer',
            showSpinner && 'animate-node-shimmer'
          )}
          style={{ width, ...borderStyle }}
          onMouseEnter={() => {
            setIsHovered(true)
            setHoveredNodeId(nodeId)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
            setHoveredNodeId(null)
          }}
        >
          {/* ReactFlow Handles */}
          {handles.map((handle) => (
            <Handle
              key={handle.id}
              type={handle.type}
              position={handle.position}
              id={handle.id}
              className="!bg-transparent !border-0 !w-2 !h-2"
              style={handle.style}
            />
          ))}

          {/* Image Area — 4-state rendering */}
          <div className="relative group p-1.5">
            {/* State 1: Image displayed (with optional running blur) */}
            <AnimatePresence mode="wait">
              {imageUrl && (
                <motion.img
                  key={imagePath}
                  src={imageUrl}
                  alt={agentTypeLabel}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className={cn(
                    `w-full ${imageAspect} object-cover block rounded-[20px]`,
                    showSpinner && 'blur-sm brightness-50 transition-all duration-300'
                  )}
                />
              )}
            </AnimatePresence>

            {/* State 2: Running overlay with existing image */}
            {showSpinner && imageUrl && (
              <div className="absolute inset-1.5 rounded-[20px] flex flex-col items-center justify-center">
                <div
                  className={`w-5 h-5 border-2 border-t-transparent rounded-full ${spinClass}`}
                  style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
                />
                <span className="text-sm text-white mt-3 font-medium">
                  {isRunning
                    ? `Refining ${agentTypeLabel.toLowerCase()}`
                    : isPendingCost
                      ? 'Waiting for cost approval...'
                      : `Queued — waiting for dependencies...`}
                </span>
              </div>
            )}

            {/* State 3: Running/pending without image */}
            {!imageUrl && showSpinner && (
              <div
                className={`w-full ${imageAspect} bg-surface-1/50 rounded-[20px] flex flex-col items-center justify-center`}
              >
                <div
                  className={`w-5 h-5 border-2 border-t-transparent rounded-full ${spinClass}`}
                  style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
                />
                <span className="text-xxs text-text-muted mt-3 text-center">
                  {isRunning
                    ? (progressMessage ||
                      `Creating${nodeName ? ` ${nodeName}` : ` ${agentTypeLabel.toLowerCase()}`}...`)
                    : isPendingCost
                      ? 'Waiting for cost approval...'
                      : 'Queued — waiting for dependencies...'}
                </span>
              </div>
            )}

            {/* State 4: Empty */}
            {!imageUrl && !showSpinner && (
              <div
                className={`w-full ${imageAspect} bg-surface-1/50 rounded-[20px] flex flex-col items-center justify-center`}
              >
                <svg
                  className={`${emptyIconSize} text-text-muted/20`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d={emptyIconPath}
                  />
                </svg>
                <span className="text-xxs text-text-muted/40 mt-1">
                  No {agentTypeLabel.toLowerCase()} yet
                </span>
              </div>
            )}

            {/* Expand / download buttons */}
            {imageUrl && !showSpinner && (
              <div className="absolute top-[14px] right-[14px] flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsModalOpen(true)
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title="Expand"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
                    />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDownload()
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title="Download"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Per-designer content area (name/desc, quick info bar) */}
          {children}

          {/* Error footer */}
          {error && (
            <div className="px-3 py-2 bg-destructive/10 border-t border-destructive/30">
              <span className="text-xxs text-destructive line-clamp-2 cursor-help" title={error}>
                {error}
              </span>
            </div>
          )}
        </div>

        {/* Bottom panel (outside card to avoid overflow-hidden clipping) */}
        {bottomPanel}
      </div>

      {/* Fullscreen Lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt="Full size"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

DesignerNodeShell.displayName = 'DesignerNodeShell'
export { DesignerNodeShell }
